/**
 * 명시한 회전 표현과 손 로컬 축 보정의 계산.
 *
 * 정본은 BE `spawning_pool_templates/mapping/rotation.py` 다. 이 파일은 그 식을 **같은 순서로** 옮긴다.
 * 같은 저장값을 같은 원본 표본에 적용하면 FE 3D 와 BE 변환이 같은 자세를 내야 한다. 테스트의 기대값은
 * BE 함수를 돌려 뽑았다(`rotationMath.test.ts`).
 *
 * 실패한 회전을 항등으로 바꾸지 않는다. 계산할 수 없으면 사유를 돌려준다.
 *
 * Owns:
 * - 3×3 행렬(행 우선) 연산, Euler · quaternion · 행렬 입력의 해석과 검사, 손 보정 `C · Rx · Ry · Rz`,
 *   고정 기준 변환의 회전, 행렬 → quaternion(WXYZ).
 *
 * Does not own:
 * - 채널 · 열 선택과 시각 — `mappingPose.ts`.
 */
import type { EulerOrder, Axis, HandRotation, Orientation } from "../domain/mapping";
import { normalizeQuaternionWxyz } from "./poseMath";

export type Vec3 = readonly [number, number, number];
export type QuaternionWxyz = readonly [number, number, number, number];
/** 행 우선 3×3. 열벡터에 작용한다(`R · v`). */
export type Matrix3 = readonly [Vec3, Vec3, Vec3];

/** BE 와 같은 허용 오차 — 단위 quaternion · 직교 행렬 · det +1 검사. */
const TOLERANCE = 1e-6;

/**
 * BE `QUATERNION_MIN_NORM` 과 같은 노름 하한. 원본이 q 와 -q 를 부호 맞춤 없이 평균하면 상쇄돼 노름이 꺼진다.
 * 나눠도 회전이 돌아오지 않아 정규화 선택과 무관하게 거부한다.
 */
export const QUATERNION_MIN_NORM = 0.9;

export const IDENTITY_MATRIX: Matrix3 = [
  [1, 0, 0],
  [0, 1, 0],
  [0, 0, 1],
];

export type OrientationProblem =
  | "non_finite"
  | "kind_missing"
  | "quaternion_spec"
  | "quaternion_zero"
  | "quaternion_sign_mixed"
  | "quaternion_not_unit"
  | "euler_spec"
  | "matrix_spec"
  | "matrix_not_rotation";

export type RotationResult =
  | { readonly ok: true; readonly matrix: Matrix3 }
  | { readonly ok: false; readonly problem: OrientationProblem };

export function multiplyMatrices(a: Matrix3, b: Matrix3): Matrix3 {
  const row = (i: number): Vec3 => [
    a[i][0] * b[0][0] + a[i][1] * b[1][0] + a[i][2] * b[2][0],
    a[i][0] * b[0][1] + a[i][1] * b[1][1] + a[i][2] * b[2][1],
    a[i][0] * b[0][2] + a[i][1] * b[1][2] + a[i][2] * b[2][2],
  ];
  return [row(0), row(1), row(2)];
}

export function transposeMatrix(m: Matrix3): Matrix3 {
  return [
    [m[0][0], m[1][0], m[2][0]],
    [m[0][1], m[1][1], m[2][1]],
    [m[0][2], m[1][2], m[2][2]],
  ];
}

export function determinant(m: Matrix3): number {
  return (
    m[0][0] * (m[1][1] * m[2][2] - m[1][2] * m[2][1]) -
    m[0][1] * (m[1][0] * m[2][2] - m[1][2] * m[2][0]) +
    m[0][2] * (m[1][0] * m[2][1] - m[1][1] * m[2][0])
  );
}

// 축 이름 → 회전 행렬에서 cos/sin 이 놓이는 두 칸. BE `euler_matrix` 의 표와 같다.
const AXIS_PLANE: Readonly<Record<"X" | "Y" | "Z", readonly [number, number]>> = {
  X: [1, 2],
  Y: [2, 0],
  Z: [0, 1],
};

function axisRotation(axis: "X" | "Y" | "Z", radians: number): Matrix3 {
  const [a, b] = AXIS_PLANE[axis];
  const cells = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];
  const cosine = Math.cos(radians);
  const sine = Math.sin(radians);
  cells[a][a] = cosine;
  cells[b][b] = cosine;
  cells[a][b] = -sine;
  cells[b][a] = sine;
  return cells as unknown as Matrix3;
}

/**
 * 축 순서의 라디안 각 → 행렬. intrinsic(로컬 축)은 뒤에 곱하고 extrinsic(고정 축)은 앞에 곱한다.
 * 예) XYZ intrinsic 은 `Rx(a) · Ry(b) · Rz(c)` 다.
 */
export function eulerMatrix(
  radians: readonly [number, number, number],
  order: EulerOrder,
  intrinsic: boolean,
): Matrix3 {
  let result = IDENTITY_MATRIX;
  for (let index = 0; index < 3; index += 1) {
    const step = axisRotation(order[index] as "X" | "Y" | "Z", radians[index]);
    result = intrinsic ? multiplyMatrices(result, step) : multiplyMatrices(step, result);
  }
  return result;
}

/** 단위 quaternion(WXYZ) → 회전 행렬. */
export function quaternionToMatrix(q: QuaternionWxyz): Matrix3 {
  const [w, x, y, z] = q;
  return [
    [1 - 2 * (y * y + z * z), 2 * (x * y - w * z), 2 * (x * z + w * y)],
    [2 * (x * y + w * z), 1 - 2 * (x * x + z * z), 2 * (y * z - w * x)],
    [2 * (x * z - w * y), 2 * (y * z + w * x), 1 - 2 * (x * x + y * y)],
  ];
}

/**
 * 고정 기준 변환의 R. `source_frame` 의 벡터를 `reference_frame` 으로 옮긴다. BE `rotation.reference_matrix` 와
 * 같다. 노름이 1 ± 1e-6 밖이면 `null` 이다. BE 는 그런 값을 저장 때 거절하고 조용히 정규화하지 않는다. 허용
 * 오차 안의 차이는 나눠서 없앤다.
 */
export function referenceMatrix(q: QuaternionWxyz): Matrix3 | null {
  const norm = Math.hypot(...q);
  if (!Number.isFinite(norm) || Math.abs(norm - 1) > TOLERANCE) {
    return null;
  }
  const [w, x, y, z] = q;
  return quaternionToMatrix([w / norm, x / norm, y / norm, z / norm]);
}

// 고르지 않을 분기의 음수 제곱근이 NaN 을 내지 않게 바닥을 둔다.
function root(value: number): number {
  return Math.sqrt(Math.max(value, 0)) * 2;
}

// 첫 0 아닌 성분이 양수가 되게 하는 부호 — `q` 와 `-q` 는 같은 회전이라 하나로 정한다.
function canonicalSign(q: QuaternionWxyz): number {
  for (const component of q) {
    if (component !== 0) {
      return component < 0 ? -1 : 1;
    }
  }
  return 1;
}

/** 회전 행렬 → 단위 quaternion(WXYZ). 가장 큰 대각 성분을 고르는 Shepperd 분기다. */
export function matrixToQuaternionWxyz(m: Matrix3): [number, number, number, number] {
  const [[r00, r01, r02], [r10, r11, r12], [r20, r21, r22]] = m;
  const trace = r00 + r11 + r22;
  let choice: QuaternionWxyz;
  if (trace > 0) {
    const s = root(trace + 1);
    choice = [0.25 * s, (r21 - r12) / s, (r02 - r20) / s, (r10 - r01) / s];
  } else if (r00 > r11 && r00 > r22) {
    const s = root(1 + r00 - r11 - r22);
    choice = [(r21 - r12) / s, 0.25 * s, (r01 + r10) / s, (r02 + r20) / s];
  } else if (r11 > r22) {
    const s = root(1 + r11 - r00 - r22);
    choice = [(r02 - r20) / s, (r01 + r10) / s, 0.25 * s, (r12 + r21) / s];
  } else {
    const s = root(1 + r22 - r00 - r11);
    choice = [(r10 - r01) / s, (r02 + r20) / s, (r12 + r21) / s, 0.25 * s];
  }
  const sign = canonicalSign(choice);
  return normalizeQuaternionWxyz([
    choice[0] * sign,
    choice[1] * sign,
    choice[2] * sign,
    choice[3] * sign,
  ]);
}

/**
 * 노름이 `QUATERNION_MIN_NORM` 보다 짧은 첫 quaternion 행. 없으면 null.
 *
 * BE 는 행별 계산 전에 모든 행을 이 하한으로 먼저 검사한다. 앞쪽 행의 비단위 오류("정규화를 켜 주세요")보다
 * 이 문제를 먼저 알려야 같은 안내가 나온다. 길이 0 · 비유한 행은 행별 검사가 알리므로 건너뛴다.
 */
export function firstSignMixedRow(rows: readonly (readonly number[] | null)[]): number | null {
  const row = rows.findIndex((values) => {
    if (values === null) return false;
    const norm = Math.hypot(...values);
    return norm > 0 && norm < QUATERNION_MIN_NORM;
  });
  return row === -1 ? null : row;
}

/**
 * 원본 자세 한 행을 회전 행렬로 해석한다. BE `orientation_matrix` 와 같은 검사를 한다.
 *
 * - quaternion: 4 열과 순서가 필요하다. 비단위 값은 `normalize_quaternion` 을 켰을 때만 정규화한다.
 *   노름이 0.9 미만인 값은 켜도 거부한다.
 * - euler: 3 열과 순서 · frame · 각 단위가 필요하다.
 * - matrix: 행 우선 9 열이다. 직교 행렬이고 det 가 +1 이어야 한다.
 */
export function orientationMatrix(values: readonly number[], spec: Orientation): RotationResult {
  if (!values.every(Number.isFinite)) {
    return { ok: false, problem: "non_finite" };
  }
  switch (spec.kind) {
    case null:
      return { ok: false, problem: "kind_missing" };
    case "quaternion": {
      if (values.length !== 4 || spec.quaternion_order === null) {
        return { ok: false, problem: "quaternion_spec" };
      }
      const [a, b, c, d] = values;
      const wxyz: QuaternionWxyz = spec.quaternion_order === "wxyz" ? [a, b, c, d] : [d, a, b, c];
      const norm = Math.hypot(...wxyz);
      if (norm === 0 || !Number.isFinite(norm)) {
        return { ok: false, problem: "quaternion_zero" };
      }
      if (norm < QUATERNION_MIN_NORM) {
        return { ok: false, problem: "quaternion_sign_mixed" };
      }
      if (!spec.normalize_quaternion && Math.abs(norm - 1) > TOLERANCE) {
        return { ok: false, problem: "quaternion_not_unit" };
      }
      return {
        ok: true,
        matrix: quaternionToMatrix([
          wxyz[0] / norm,
          wxyz[1] / norm,
          wxyz[2] / norm,
          wxyz[3] / norm,
        ]),
      };
    }
    case "euler": {
      if (
        values.length !== 3 ||
        spec.euler_order === null ||
        spec.euler_frame === null ||
        spec.angle_unit === null
      ) {
        return { ok: false, problem: "euler_spec" };
      }
      const scale = spec.angle_unit === "deg" ? Math.PI / 180 : 1;
      return {
        ok: true,
        matrix: eulerMatrix(
          [values[0] * scale, values[1] * scale, values[2] * scale],
          spec.euler_order,
          spec.euler_frame === "intrinsic",
        ),
      };
    }
    case "matrix": {
      if (values.length !== 9) {
        return { ok: false, problem: "matrix_spec" };
      }
      const matrix: Matrix3 = [
        [values[0], values[1], values[2]],
        [values[3], values[4], values[5]],
        [values[6], values[7], values[8]],
      ];
      return isRotation(matrix)
        ? { ok: true, matrix }
        : { ok: false, problem: "matrix_not_rotation" };
    }
  }
}

function isRotation(matrix: Matrix3): boolean {
  const gram = multiplyMatrices(transposeMatrix(matrix), matrix);
  const orthonormal = gram.every((row, i) =>
    row.every((value, j) => Math.abs(value - (i === j ? 1 : 0)) <= TOLERANCE),
  );
  return orthonormal && Math.abs(determinant(matrix) - 1) <= TOLERANCE;
}

/** `+x` → (1, 0, 0), `-z` → (0, 0, -1). */
export function axisVector(axis: Axis): Vec3 {
  const sign = axis[0] === "-" ? -1 : 1;
  const index = "xyz".indexOf(axis[1]);
  return [index === 0 ? sign : 0, index === 1 ? sign : 0, index === 2 ? sign : 0];
}

/** 두 축이 직교하는지. 같은 축의 양 · 음 방향은 평행이다. */
export function areAxesOrthogonal(xAxis: Axis, yAxis: Axis): boolean {
  return xAxis[1] !== yAxis[1];
}

function cross(a: Vec3, b: Vec3): Vec3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
}

/**
 * 손 보정 `C · Rx(α) · Ry(β) · Rz(γ)`. `C = [a, b, a × b]` 는 새 손 X · Y · Z 축을 열로 둔 행렬이다.
 * 각도는 도 단위이고 로컬 X → Y → Z 순서로 곱한다. 직교하지 않는 두 축은 거부한다.
 * 예) `+x` / `+y` 와 0 도는 항등이다.
 */
export function handMatrix(rotation: HandRotation): Matrix3 | null {
  if (!areAxesOrthogonal(rotation.x_axis, rotation.y_axis)) {
    return null;
  }
  const x = axisVector(rotation.x_axis);
  const y = axisVector(rotation.y_axis);
  const z = cross(x, y);
  const basis: Matrix3 = [
    [x[0], y[0], z[0]],
    [x[1], y[1], z[1]],
    [x[2], y[2], z[2]],
  ];
  const toRadians = Math.PI / 180;
  const [alpha, beta, gamma] = rotation.degrees;
  return multiplyMatrices(
    basis,
    eulerMatrix([alpha * toRadians, beta * toRadians, gamma * toRadians], "XYZ", true),
  );
}
